/**
 * IndustrySelector Component
 *
 * Purpose: Displays industry/business type options for the selected state
 * User Flow: This is Step 2 - User selects their industry/business type
 * Data Source: industryData.js and businessTypes.js
 */

import React, { useState, useMemo } from 'react';
import { useCalculatorContext } from '../contexts/CalculatorContext';
import { industryData, businessTypes, stateData } from '../data';

const IndustrySelector = () => {
  const { state: contextState, actions } = useCalculatorContext();
  const [expandedSector, setExpandedSector] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  // Group business types by industry sector
  const groupedBusinesses = useMemo(() => {
    const grouped = {};
    
    businessTypes.forEach((business) => {
      const sectorId = business.industrySector;
      
      if (!grouped[sectorId]) {
        grouped[sectorId] = {
          sector: industryData[sectorId],
          businesses: []
        };
      }
      
      // Filter out state-restricted businesses if applicable
      if (business.stateRestricted && business.stateAvailability) {
        if (business.stateAvailability.includes(contextState.selectedState)) {
          grouped[sectorId].businesses.push(business);
        }
      } else {
        grouped[sectorId].businesses.push(business);
      }
    });
    
    return grouped;
  }, [contextState.selectedState]);

  // Handle sector expansion
  const handleSectorToggle = (sectorId) => {
    setExpandedSector(expandedSector === sectorId ? null : sectorId);
  };

  // Handle business type selection
  const handleBusinessSelect = (businessTypeId) => {
    setSelectedBusiness(businessTypeId);
    
    // Set business type and trigger calculation
    const success = actions.setBusinessType(businessTypeId);
    if (success) {
      // Trigger calculation
      actions.calculate(contextState.selectedState, businessTypeId);
    }
  };

  // Handle back button
  const handleBack = () => {
    actions.goBack();
  };

  return (
    <div className="fss-industry-selector">
      <div className="fss-selector-header">
        <h2>What type of food business do you operate?</h2>
        <p className="fss-subtitle">
          Selected state: <strong>{stateData[contextState.selectedState]?.name}</strong>
        </p>
      </div>

      {/* Back button */}
      <button 
        className="fss-back-button" 
        onClick={handleBack}
        aria-label="Go back to state selection"
      >
        ← Change State
      </button>

      {/* Display any errors or warnings */}
      {contextState.errors.length > 0 && (
        <div className="fss-error-messages">
          {contextState.errors.map((error, index) => (
            <div key={index} className="fss-error-message">
              <span className="fss-error-icon">⚠️</span>
              {error}
            </div>
          ))}
        </div>
      )}

      {contextState.warnings.length > 0 && (
        <div className="fss-warning-messages">
          {contextState.warnings.map((warning, index) => (
            <div key={index} className={`fss-warning-message fss-warning-${warning.type}`}>
              <span className="fss-warning-icon">ℹ️</span>
              {warning.message}
            </div>
          ))}
        </div>
      )}

      {/* Industry categories */}
      <div className="fss-industry-categories">
        {Object.keys(groupedBusinesses).map((sectorId) => {
          const { sector, businesses } = groupedBusinesses[sectorId];
          const isExpanded = expandedSector === sectorId;

          return (
            <div key={sectorId} className="fss-category-section">
              {/* Sector header */}
              <button
                className={`fss-category-header ${isExpanded ? 'fss-active' : ''}`}
                onClick={() => handleSectorToggle(sectorId)}
                aria-expanded={isExpanded}
              >
                <div className="fss-category-content">
                  <h3>{sector.name}</h3>
                  <p className="fss-category-description">{sector.description}</p>
                  
                  {/* Show business count */}
                  <span className="fss-business-count">
                    {businesses.length} {businesses.length === 1 ? 'type' : 'types'}
                  </span>
                  
                  {/* Show sector badges */}
                  <div className="fss-sector-badges">
                    {sector.highRisk && (
                      <span className="fss-badge fss-badge-high-risk">High Risk Category</span>
                    )}
                    {sector.vulnerablePopulation && (
                      <span className="fss-badge fss-badge-vulnerable">Vulnerable Population</span>
                    )}
                  </div>
                </div>
                
                <span className="fss-expand-icon">{isExpanded ? '−' : '+'}</span>
              </button>

              {/* Business type list */}
              {isExpanded && (
                   <div className={`fss-business-types ${isExpanded ? 'fss-expanded' : ''}`}>
                  {businesses.map((business) => (
                    <button
                      key={business.id}
                      className={`fss-business-type-button ${selectedBusiness === business.id ? 'fss-selected' : ''}`}
                      onClick={() => handleBusinessSelect(business.id)}
                      disabled={contextState.isLoading}
                    >
                      <div className="fss-business-content">
                        <strong className="fss-business-name">{business.name}</strong>
                        <span className="fss-business-description">{business.description}</span>
                        
                        {/* Show category badge */}
                        <span className="fss-category-badge">{business.category}</span>
                        
                        {/* Show special indicators */}
                        {business.highRisk && (
                          <span className="fss-indicator fss-high-risk" title="High-risk business">
                            ⚠️ High risk business category
                          </span>
                        )}
                        {business.vulnerablePopulation && (
                          <span className="fss-indicator fss-vulnerable" title="Serves vulnerable population">
                            👥 Serves vulnerable population
                          </span>
                        )}
                        {business.requiresSubSelection && (
                          <span className="fss-indicator fss-sub-selection" title="Additional selection required">
                            📋 Additional selection required
                          </span>
                        )}
                        {business.stateRestricted && (
                          <span className="fss-indicator fss-restricted" title="Limited availability in some states">
                            🔒 Limited availability in some states
                          </span>
                        )}
                      </div>
                      
                      {/* Show checkmark if selected */}
                      {selectedBusiness === business.id && (
                        <span className="fss-checkmark">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Loading indicator */}
      {contextState.isLoading && (
        <div className="fss-loading-overlay">
          <div className="fss-loading-spinner"></div>
          <p>Calculating your requirements...</p>
        </div>
      )}

      {/* Help section */}
      <div className="fss-help-section">
        <details className="fss-help-details">
          <summary><strong>Not sure which category fits your business?</strong></summary>
          <div className="fss-help-content">
            <p>Here's a quick guide:</p>
            <ul>
              <li><strong>Hospitality & Retail:</strong> If you serve food directly to customers in a restaurant, cafe, or similar setting</li>
              <li><strong>Health & Community:</strong> If you work in hospitals, schools, or childcare</li>
              <li><strong>Aged Care:</strong> If you provide food services in aged care facilities</li>
              <li><strong>Food Processing:</strong> If you manufacture or process food products</li>
            </ul>
            <p>
              <strong>Still need help?</strong> <a href="/contact">Contact CFT</a> for guidance.
            </p>
          </div>
        </details>
      </div>

      {/* State-specific notes */}
      {contextState.selectedState && (
        <div className="fss-state-notes">
          {contextState.selectedState === 'VIC' && (
            <div className="fss-note fss-note-info">
              <strong>Victoria Note:</strong> While FSS is not legally mandated in Victoria, 
              councils and auditors expect documented training. We recommend obtaining certification.
            </div>
          )}
          {contextState.selectedState === 'NSW' && (
            <div className="fss-note fss-note-info">
              <strong>NSW Note:</strong> Aged care facilities have specific requirements. 
              Licensed facilities need a written Food Safety Program.
            </div>
          )}
          {contextState.selectedState === 'QLD' && (
            <div className="fss-note fss-note-warning">
              <strong>QLD Note:</strong> Remember to notify your local council within 14 days 
              of appointing a Food Safety Supervisor.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IndustrySelector;
